import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { ExtendedBaseTab } from '@common/classes/extended-base-tab';
import { PcsDocumentAndApprovalStatusCodes } from '@common/knownTypes/PcsDocumentAndApprovalStatusCodes';
import { CodelistDisplayModel } from '@common/models/common/CodelistDisplay.model';
import { ModalService } from '@common/services/modal.service';
import { marker } from '@common/translate-marker';
import { parsePcsApprovalRecipient } from '@common/utils/pcs-approval-recipient.util';
import { DialogCloseResult, DialogService } from '@progress/kendo-angular-dialog';
import { GridComponent } from '@progress/kendo-angular-grid';
import { PcsDocumentAndApprovalServices } from '@services/pcs-document-and-approval-services.service';
import { CustomExtendedTabService } from 'app/singleton-services/custom-extended-tab.service';
import { filter, Subscription } from 'rxjs';
import { LastUpdateService } from '../../../../singleton-services/last-update.service';
import { StateService } from '../../../../singleton-services/state.service';
import { AdditionalDataSaveModel } from '../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/AdditionalDataSaveModel';
import { PcsApprovalGridDisplayModel } from '../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/OtherDisplayModels/PcsApprovalGridDisplayModel';
import { PcsApprovalNewPossibleApprovalsDisplayModel } from '../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/OtherDisplayModels/PcsApprovalNewPossibleApprovalsDisplayModel';
import { PcsDocumentOrApprovalUpdateGridDateDisplayModel } from '../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/OtherDisplayModels/PcsDocumentOrApprovalUpdateGridDateDisplayModel';
import { PcsNewApprovalSaveModel } from '../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/SaveModels/PcsNewApprovalSaveModel';
import { PcsDocumentOrApprovalLocation } from '../models/PcsDocumentOrApprovalLocation';
import { AdditionalDataInputComponent } from '../tabs-approval-documents/pcs-tab-additional-data/additional-data-input/additional-data-input.component';

@Component({
    selector: 'app-tab-pcs-additional-approvals',
    templateUrl: './tab-pcs-additional-approvals.component.html',
    styleUrls: ['./tab-pcs-additional-approvals.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabPcsAdditionalApprovalsComponent
    extends ExtendedBaseTab
    implements OnInit, AfterViewChecked, OnChanges, OnDestroy
{
    @Input() correspondingId: number;
    @Input() approvalLocation: PcsDocumentOrApprovalLocation;
    @Input() actionConditions = true;
    @Input() generalActStatusId = null;
    @Output() approvalChanged = new EventEmitter<void>();
    @ViewChildren('inputForAdditionalData') inputs: QueryList<ElementRef>;
    @ViewChildren('additionalDataInputs') additionalDataInputs: AdditionalDataInputComponent[];

    pcsApprovals: PcsApprovalGridDisplayModel[];
    newApprovalModalOpened = false;
    newPossibleApprovals: PcsApprovalNewPossibleApprovalsDisplayModel[];
    selectedApproval: PcsApprovalNewPossibleApprovalsDisplayModel;
    currentSelectedTypeId = -1;
    disabledButtonWhileProcessing = false;

    approvalModalOpened = false;

    possibleOrganizationSelections: CodelistDisplayModel[];
    sendApprovalDefaultOrganization: CodelistDisplayModel | null;
    currentSelection: any;
    selectedOrganization: string | number | null;
    selectedTerminal: string | null;
    approvalId: number;
    possibleStateChanges: any[] = [];
    actionApprovalForConfirm: any = null;
    showChangeOrganization: boolean = false;
    rejectionRemarkChanges = false;
    multipleDeliveryOrderApprovals: boolean = false;

    rejectionRemarkDialogOpened = false;
    remarkValue = '';
    rejectionApproval: any;

    private initialized = false;
    private approvalsSubscription: Subscription;
    private possibleApprovalsSubscription: Subscription;

    constructor(
        private service: PcsDocumentAndApprovalServices,
        private kendoDialogService: DialogService,
        private modalService: ModalService,
        public stateService: StateService,
        public router: Router,
        public lastUpdateService: LastUpdateService,
        private cdr: ChangeDetectorRef,
        customExtendedTabService: CustomExtendedTabService
    ) {
        super(customExtendedTabService);
    }

    ngOnInit(): void {
        this.initialized = true;
        this.reloadForCurrentDocument();
    }

    /** Reloads the approvals grid - used by parent screens whose header actions
     * change approval statuses in the background (e.g. SR Approve/Decline updating SR-TO) */
    public refresh(): void {
        this.isBusy = true;
        this.getPcsAdditionalApprovalTab();
        this.getAllPossibleNewDocumentsOrApprovals();
    }

    ngAfterViewChecked() {
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            this.initialized &&
            ['correspondingId', 'approvalLocation'].some(
                (input) => changes[input] && changes[input].previousValue !== changes[input].currentValue
            )
        ) {
            this.reloadForCurrentDocument();
        }
    }

    ngOnDestroy(): void {
        this.approvalsSubscription?.unsubscribe();
        this.possibleApprovalsSubscription?.unsubscribe();
    }

    private reloadForCurrentDocument(): void {
        this.approvalsSubscription?.unsubscribe();
        this.possibleApprovalsSubscription?.unsubscribe();
        this.closeNewApprovalModal();
        this.clearSelected();
        this.defaultPaging();
        this.pcsApprovals = [];
        this.newPossibleApprovals = [];
        this.loadItems(this.pcsApprovals);

        if (this.correspondingId && this.approvalLocation) {
            this.refresh();
        }
    }

    openNewApprovalModal(): void {
        this.newApprovalModalOpened = true;
    }

    closeNewApprovalModal(): void {
        this.newApprovalModalOpened = false;
        this.currentSelectedTypeId = -1;
        this.selectedApproval = null;
        this.resetStatusChangeDialogState();
    }

    public changeCurrentApprovalType($event: any) {
        if (!$event) {
            this.currentSelectedTypeId = -1;
            return;
        }
        this.currentSelectedTypeId = $event.id;
    }

    public createNewApproval(): void {
        if (this.currentSelectedTypeId === -1) {
            return;
        }
        this.disabledButtonWhileProcessing = true;
        const additionalDataToSend: AdditionalDataSaveModel[] = [];

        let mandatoryDataNotFound = false;

        this.additionalDataInputs.forEach((input) => {
            const newData = input.getRawValue();
            if ((newData.value === null || newData.value === '') && input.isMandatory()) {
                mandatoryDataNotFound = true;
            }
            additionalDataToSend.push(newData);
        });

        if (mandatoryDataNotFound) {
            this.disabledButtonWhileProcessing = false;
            return;
        }

        const saveModel: PcsNewApprovalSaveModel = {
            isForApproval: true,
            correspondingId: this.correspondingId,
            pcsApprovalTypeId: this.currentSelectedTypeId,
            additionalData: additionalDataToSend,
            location: this.approvalLocation
        };
        this.service.createNewPcsDocumentOrApproval(saveModel).subscribe((response) => {
            if (response.id !== -1) {
                /*this.pcsApprovals.push(response);
                this.loadItems(this.pcsApprovals);*/

                // reload from backend instead of pushing to model
                this.isBusy = true;
                this.getPcsAdditionalApprovalTab();
                this.getAllPossibleNewDocumentsOrApprovals();
                this.approvalChanged.emit();
            }
            this.newApprovalModalOpened = false;
            this.disabledButtonWhileProcessing = false;
        });
    }

    public updateData($event: PcsDocumentOrApprovalUpdateGridDateDisplayModel, id): void {
        const changeRow = this.pcsApprovals.find((item) => item.id === id);
        const approvalStateChanged =
            changeRow.pcsDocumentAndApprovalStatus?.id !== $event.status?.id ||
            changeRow.forOrganization?.id !== $event.forOrganization?.id;
        changeRow.forOrganization = $event.forOrganization;
        changeRow.lastModifiedDate = $event.lastModifiedDate;
        changeRow.lastModifiedBy = $event.lastModifiedBy;
        changeRow.pcsDocumentAndApprovalStatus = $event.status;
        changeRow.hasAttachments = $event.hasAttachments;
        changeRow.remarks = $event.remarks;
        if (approvalStateChanged) {
            this.refreshApprovalActions(changeRow);
        }
        this.approvalChanged.emit();
    }

    private refreshApprovalActions(approval: PcsApprovalGridDisplayModel): void {
        this.service
            .getAllPossibleStateTransitions({ isForApproval: true, documentOrApprovalId: +approval.id })
            .subscribe((transitions) => {
                const statuses = transitions.map((transition) => transition.toStatus.id);
                approval.canApprove = statuses.includes('APP');
                approval.canReject = statuses.includes('REJ');
                approval.canSendForApproval = statuses.includes('WFA');
                approval.canSend2ndApproval = statuses.includes('2ND_WFA');
                approval.canSend3rdApproval = statuses.includes('3RD_WFA');
                approval.canComplete = statuses.includes('NFA');
            });
    }

    public getAllAdditionalDataToDisplay(): {
        dataName: string;
        dataRole: string;
        dataFormat: string;
        dataRoleId: number;
    }[] {
        if (!this.newPossibleApprovals || !this.currentSelectedTypeId) {
            return [];
        }

        return (
            this.newPossibleApprovals
                .find((npa) => npa.id === this.currentSelectedTypeId)
                ?.additionalDataInputs?.filter((adi) => adi.dataRole !== 'N') ?? []
        );
    }

    public getElementFromFormat(editor: string): string {
        switch (editor.toLowerCase()) {
            case 'string':
            case 'email':
                return 'TextBox';
            case 'datetime':
                return 'DateTime';
            case 'boolean':
                return 'Checkbox';
            case 'int':
            case 'decimal':
                return 'NumericTextBox';
            default:
                return '';
        }
    }

    getClass(status): string {
        return this.resolvePcsDocumentStatusPillClass(status);
    }

    public openPcsApprovalAttachments(rowId: number, status: string): void {
        this.service.getPcsApprovalAttachments(rowId).subscribe((attachments) => {
            this.modalService
                .openAttachmentList({
                    pcsAttachments: attachments,
                    pcsDocumentAndApprovalStatusId: status,
                    openedAttachmentId: rowId
                })
                .subscribe(() => {
                    this.loadItems(this.pcsApprovals);
                });
        });
    }

    isAnyInputRequired(): boolean {
        const inputComponents: AdditionalDataInputComponent[] = Array.from(this.additionalDataInputs);
        return inputComponents.some((input) => input.isMandatory() && (input.value === null || input.value === ''));
    }

    private getPcsAdditionalApprovalTab() {
        this.approvalsSubscription?.unsubscribe();

        if (!this.correspondingId || !this.approvalLocation) {
            this.isBusy = false;
            return;
        }

        this.approvalsSubscription = this.service
            .getPcsAdditionalApprovalTab(this.approvalLocation, this.correspondingId)
            .subscribe({
                next: (response) => {
                    this.pcsApprovals = response;
                    this.loadItems(this.pcsApprovals);
                },
                error: () => {
                    this.pcsApprovals = [];
                    this.loadItems(this.pcsApprovals);
                }
            });
    }

    private getAllPossibleNewDocumentsOrApprovals() {
        this.possibleApprovalsSubscription?.unsubscribe();

        if (!this.correspondingId || !this.approvalLocation) {
            return;
        }

        this.possibleApprovalsSubscription = this.service
            .getAllPossibleNewDocumentsOrApprovals(this.approvalLocation, true)
            .subscribe({
                next: (response) => {
                    if (this.approvalLocation === PcsDocumentOrApprovalLocation.VESSEL_NOTIFICATION_ARRIVAL) {
                        this.selectedApproval = response?.find((item) => item?.id.toString() === '8');
                        this.currentSelectedTypeId = this.selectedApproval?.id;
                    } else if (this.approvalLocation === PcsDocumentOrApprovalLocation.VESSEL_NOTIFICATION_DEPARTURE) {
                        this.selectedApproval = response?.find((item) => item?.id.toString() === '9');
                        this.currentSelectedTypeId = this.selectedApproval?.id;
                    }
                    this.newPossibleApprovals = response;
                },
                error: () => {
                    this.newPossibleApprovals = [];
                }
            });
    }

    openApprovalModal(approvalId: string) {
        this.loadPossibleStateTransitions(approvalId);
        this.approvalId = +approvalId;
        this.approvalModalOpened = true;
    }

    canUserOrgSendApproval(dataItem: any) {
        return !!dataItem.canSendForApproval;
    }

    loadPossibleStateTransitions(approvalId: any) {
        this.sendApprovalDefaultOrganization = null;
        this.possibleOrganizationSelections = [];

        this.service
            .getAllPossibleStateTransitions({
                isForApproval: false,
                documentOrApprovalId: approvalId
            })
            .subscribe((response) => {
                this.currentSelection = response.find((item) => item.toStatus.id === 'WFA');
                this.possibleOrganizationSelections = this.currentSelection.possibleOrganizationsForApproval;

                if (
                    this.currentSelection.defaultOrganizationsForApproval &&
                    this.currentSelection.defaultOrganizationsForApproval.length === 1
                ) {
                    this.sendApprovalDefaultOrganization = this.currentSelection.defaultOrganizationsForApproval[0];
                    this.changeSelectedOrganization(this.sendApprovalDefaultOrganization);
                }
            });
    }

    public changeSelectedOrganization($event) {
        const recipient = parsePcsApprovalRecipient($event);
        this.selectedOrganization = recipient.forOrganization;
        this.selectedTerminal = recipient.terminalId;
    }

    private submitConfirmDialog() {
        if (this.currentSelection === null || (this.selectedOrganization === null && this.showChangeOrganization))
            return;

        if (this.rejectionRemarkChanges && this.remarkValue !== '') {
            this.service
                .newRemark({
                    isForApproval: false,
                    pcsDocumentOrApprovalId: this.actionApprovalForConfirm.id,
                    remark: this.remarkValue
                })
                .subscribe(() => {
                    this.submitActionStatusChangeForList();
                });
        } else {
            this.submitActionStatusChangeForList();
        }
    }

    public closeConfirmDialog() {
        this.resetStatusChangeDialogState();
    }

    public closeRejectionRemarksDialog() {
        this.resetStatusChangeDialogState();
    }

    public submitRejectionRemark() {
        this.rejectionRemarkDialogOpened = false;
        this.service
            .newRemark({
                isForApproval: false,
                pcsDocumentOrApprovalId: this.rejectionApproval.id,
                remark: this.remarkValue
            })
            .subscribe(() => {
                this.remarkValue = '';
                this.submitActionStatusChange(this.rejectionApproval, false);
            });
    }

    public searchOrganizations(filter: string) {
        if (this.currentSelection !== undefined) {
            this.possibleOrganizationSelections = this.currentSelection.possibleOrganizationsForApproval.filter(
                (org) => org.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
            );
        }
    }

    canUserApproveApproval(dataItem: any) {
        return !!dataItem.canApprove;
    }

    canUserRejectApproval(dataItem: any) {
        return !!dataItem.canReject;
    }

    requiresMyApproval(dataItem: any): boolean {
        const statusId = dataItem.pcsDocumentAndApprovalStatus?.id;
        const forOrgId = dataItem.forOrganization?.id;
        if (!statusId || !forOrgId) return false;

        const waitingStatus = PcsDocumentAndApprovalStatusCodes.WaitingForApproval.includes(statusId);
        const isForMyOrg = forOrgId === this.user.organization.id.toString();
        const canApprove = !!dataItem.canApprove;

        return waitingStatus && isForMyOrg && !canApprove;
    }

    public changeStatus(action: string, approval?: any) {
        if (!approval) return;

        this.actionApprovalForConfirm = approval;
        this.multipleDeliveryOrderApprovals = !!approval.multipleDeliveryOrderApprovals;
        this.showChangeOrganization = false;
        this.possibleOrganizationSelections = [];
        this.sendApprovalDefaultOrganization = null;
        this.selectedOrganization = null;
        this.selectedTerminal = null;

        this.service
            .getAllPossibleStateTransitions({ isForApproval: false, documentOrApprovalId: approval.id })
            .subscribe((response) => {
                this.possibleStateChanges = response;

                if (action === 'complete') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === 'NFA');
                }

                if (action === 'approve') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === 'APP');
                    this.selectedOrganization = approval.forOrganization?.id;
                    this.selectedTerminal = approval.terminalId;
                }

                if (action === 'delete') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === 'DEL');
                }

                if (action === 'sendApproval') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === 'WFA');

                    if (
                        this.currentSelection?.defaultOrganizationsForApproval &&
                        this.currentSelection.defaultOrganizationsForApproval.length === 1
                    ) {
                        this.sendApprovalDefaultOrganization = this.currentSelection.defaultOrganizationsForApproval[0];
                        this.changeSelectedOrganization(this.sendApprovalDefaultOrganization);
                    }

                    this.possibleOrganizationSelections = this.currentSelection?.possibleOrganizationsForApproval || [];
                    this.showChangeOrganization = false;
                    this.approvalId = +approval.id;
                    this.approvalModalOpened = true;
                }

                if (action === 'reject') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === 'REJ');
                    this.selectedOrganization = approval.forOrganization?.id;
                    this.selectedTerminal = approval.terminalId;
                }

                if (action === 'onHold') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === 'ONH');
                }

                if (action === 'send2ndApproval') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === '2ND_WFA');
                    this.changeCurrentStateTransition(this.currentSelection);
                    this.possibleOrganizationSelections = this.currentSelection?.possibleOrganizationsForApproval || [];
                    this.showChangeOrganization = true;
                }

                if (action === 'send3rdApproval') {
                    this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus?.id === '3RD_WFA');
                    this.changeCurrentStateTransition(this.currentSelection);
                    this.possibleOrganizationSelections = this.currentSelection?.possibleOrganizationsForApproval || [];
                    this.showChangeOrganization = true;
                }

                if (this.currentSelection && !this.showChangeOrganization) {
                    this.openStatusChangeConfirm();
                }
            });
    }

    public changeCurrentStateTransition($event): void {
        if (!$event) {
            this.currentSelection = null;
            this.possibleOrganizationSelections = [];
            this.showChangeOrganization = false;
            return;
        }
        this.currentSelection = $event;
        if (this.currentSelection.toStatus.id.includes('WFA')) {
            this.showChangeOrganization = true;
            this.possibleOrganizationSelections = this.currentSelection.possibleOrganizationsForApproval;
        } else {
            this.showChangeOrganization = false;
            this.possibleOrganizationSelections = [];
        }
    }

    public newStateTransition(): void {
        if (this.currentSelection) {
            // the confirm opens in the app-level dialog container, which renders under this inline dialog
            this.showChangeOrganization = false;
            this.openStatusChangeConfirm();
        }
    }

    closeChangeOrganizationModal(): void {
        this.resetStatusChangeDialogState();
    }

    submitApprovalActionStatusChange() {
        this.service
            .newActionStatusChange({
                pcsDocumentOrApprovalId: this.approvalId,
                statusChangeId: this.currentSelection.id,
                isForApproval: false,
                forOrganization: parsePcsApprovalRecipient(this.selectedOrganization).forOrganization,
                terminalId: this.selectedTerminal
            })
            .subscribe(() => {
                this.resetStatusChangeDialogState();
                this.isBusy = true;
                this.getPcsAdditionalApprovalTab();
                this.getAllPossibleNewDocumentsOrApprovals();
                this.approvalChanged.emit();
            });
    }

    private submitActionStatusChangeForList() {
        this.service
            .newActionStatusChange({
                pcsDocumentOrApprovalId: this.actionApprovalForConfirm.id,
                statusChangeId: this.currentSelection.id,
                isForApproval: false,
                forOrganization: parsePcsApprovalRecipient(this.selectedOrganization).forOrganization,
                terminalId: this.selectedTerminal
            })
            .subscribe({
                next: () => {
                    this.resetStatusChangeDialogState();

                    this.isBusy = true;
                    this.getAllPossibleNewDocumentsOrApprovals();
                    this.getPcsAdditionalApprovalTab();
                    this.approvalChanged.emit();
                },
                error: (err) => {
                    if (err.error?.errorMessage?.includes('version mismatch')) {
                        setTimeout(() => {
                            window.location.reload();
                        }, 3000);
                    }
                }
            });
    }

    private openStatusChangeConfirm(): void {
        if (!this.currentSelection) {
            return;
        }

        this.dialogService
            .confirm(
                this.getStatusChangeConfirmTitle(),
                this.getStatusChangeConfirmMessage(),
                this.translateService.instant(marker('Submit')),
                null,
                this.translateService.instant(marker('Cancel'))
            )
            .subscribe((result) => {
                if (result === true) {
                    this.submitConfirmDialog();
                    return;
                }

                if (result === false || result instanceof DialogCloseResult) {
                    this.closeConfirmDialog();
                }
            });
    }

    private getStatusChangeConfirmTitle(): string {
        return this.translateService.instant(
            marker(
                this.multipleDeliveryOrderApprovals && this.currentSelection?.toStatus?.id === 'APP'
                    ? 'Approve'
                    : 'Confirm change status'
            )
        );
    }

    private getStatusChangeConfirmMessage(): string {
        if (this.multipleDeliveryOrderApprovals && this.currentSelection?.toStatus?.id === 'APP') {
            return this.translateService.instant(
                marker(
                    'There are other pending approvals for this delivery order. Do you still want to proceed with approval?'
                )
            );
        }

        const fromStatusName = this.currentSelection?.fromStatus?.name
            ? this.translateService.instant(this.currentSelection.fromStatus.name)
            : '';
        const toStatusName = this.currentSelection?.toStatus?.name
            ? this.translateService.instant(this.currentSelection.toStatus.name)
            : '';

        return `${this.translateService.instant(
            marker('Are you sure you want to change status from')
        )} ${fromStatusName} ${this.translateService.instant(marker('to'))} ${toStatusName}?`;
    }

    public openRejectionRemarkDialog(approval: any) {
        this.rejectionApproval = approval;
        this.rejectionRemarkDialogOpened = true;
    }

    submitActionStatusChange(approval: any, isForApproval: boolean = true) {
        const approvalMessage =
            approval.multipleDeliveryOrderApprovals && isForApproval
                ? this.translateService.instant(
                      marker(
                          'There are other pending approvals for this delivery order. Do you still want to proceed with approval?'
                      )
                  )
                : this.translateService.instant(
                      marker(`Are you sure you want to ${isForApproval ? 'confirm' : 'reject'} this approval?`)
                  );
        this.dialogService
            .confirm(
                this.translateService.instant(marker(`${isForApproval ? 'Approve' : 'Reject'}`)),
                approvalMessage,
                this.translateService.instant(marker('YES')),
                this.translateService.instant(marker('NO')),
                null
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)))
            .subscribe((result) => {
                if (!result) return;
                this.service
                    .newActionStatusChange({
                        pcsDocumentOrApprovalId: approval.id,
                        statusChangeId: 0,
                        isForApproval,
                        forOrganization: parsePcsApprovalRecipient(approval.forOrganization?.id).forOrganization,
                        terminalId: null
                    })
                    .subscribe(() => {
                        this.resetStatusChangeDialogState();
                        this.getPcsAdditionalApprovalTab();
                        this.getAllPossibleNewDocumentsOrApprovals();
                        this.approvalChanged.emit();
                    });
            });
    }

    // don't wan't Actions column resizing since it won't use declared width=350 as specified in html template
    protected expandLastColumnToFit(grid: GridComponent = this.grid) {
        const columns: any[] = grid.columnList.toArray();

        const actionsIndex = columns.findIndex((c) => c.field === 'actions');

        const columnsToAutoFit = actionsIndex >= 0 ? columns.filter((_, index) => index !== actionsIndex) : columns;

        grid.autoFitColumns(columnsToAutoFit);
    }

    public closeStatusChangeDialogs(): void {
        this.resetStatusChangeDialogState();
    }

    private resetStatusChangeDialogState(): void {
        this.currentSelection = null;
        this.possibleStateChanges = [];
        this.possibleOrganizationSelections = [];
        this.approvalModalOpened = false;
        this.showChangeOrganization = false;
        this.selectedOrganization = null;
        this.selectedTerminal = null;
        this.sendApprovalDefaultOrganization = null;
        this.actionApprovalForConfirm = null;
        this.approvalId = 0;
        this.rejectionApproval = null;
        this.rejectionRemarkDialogOpened = false;
        this.rejectionRemarkChanges = false;
        this.remarkValue = '';
        this.multipleDeliveryOrderApprovals = false;
    }
}
